import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";

const Shell = ({ company, children }) => (
  <div className="min-h-screen bg-slate-100 flex items-start justify-center p-4 pt-10">
    <div className="bg-white rounded-lg shadow-lg border-t-4 border-emerald-600 w-full max-w-md p-6" data-testid="tax-info-card">
      <div className="flex items-center gap-2 mb-3">
        <img src={`${API_BASE}/settings/company/logo`} alt="SOS" className="h-8 w-8 rounded object-contain" />
        <div className="text-xs uppercase tracking-widest text-slate-400 font-bold">{company || "Safety Onsite Solutions"}</div>
      </div>
      {children}
    </div>
  </div>
);

// Public (no login): worker submits SSN/EIN, legal/business name and mailing address.
export default function TaxInfoPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({ tax_id: "", legal_name: "", dba: "", address: "" });

  useEffect(() => {
    api.get(`/public/tax-info/${token}`)
      .then((r) => { setData(r.data); setForm({ tax_id: "", legal_name: r.data.worker_name, dba: r.data.dba, address: r.data.address }); })
      .catch((e) => setErr(e.response?.data?.detail?.toString() || "This link is not valid."));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const r = await api.post(`/public/tax-info/${token}`, form);
      setDone(r.data);
    } catch (e2) { setErr(e2.response?.data?.detail?.toString() || e2.message); }
    finally { setBusy(false); }
  };

  const company = data?.company_name;
  if (err && !data) return <Shell company={company}><div data-testid="tax-info-error" className="text-rose-600 font-semibold">{err}</div></Shell>;
  if (!data) return <Shell company={company}><div className="text-slate-500">Loading…</div></Shell>;
  if (done) {
    return (
      <Shell company={company}>
        <div data-testid="tax-info-done">
          <h1 className="text-xl font-black text-emerald-700">✓ Thank you</h1>
          <p className="mt-2 text-sm text-slate-700">Your {done.updated.join(", ") || "information"} has been saved securely. The office has been notified. Nothing else to do.</p>
        </div>
      </Shell>
    );
  }
  const needTin = data.needs.some((n) => n.startsWith("tax ID"));
  const needAddr = data.needs.some((n) => n.startsWith("mailing"));
  const inp = "mt-1 w-full h-12 px-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500";
  return (
    <Shell company={company}>
      <h1 className="text-xl font-black text-slate-900">Hi {data.worker_name.split(" ")[0]} — tax information</h1>
      <p className="mt-2 text-sm text-slate-600">We need this to prepare your year-end tax form (1099-NEC). It's encrypted and used only for tax reporting.</p>
      <form onSubmit={submit} className="mt-4 space-y-4">
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-slate-500">SSN or EIN {needTin && "*"}</label>
          <input required={needTin} inputMode="numeric" autoComplete="off" value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
            placeholder={data.tax_id_last4 ? `on file ending ${data.tax_id_last4} — leave blank to keep` : "###-##-#### or ##-#######"}
            data-testid="tax-info-tin" className={`${inp} font-mono`} />
          <div className="text-xs text-slate-500 mt-1">Use your EIN only if you're paid through your business.</div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Legal name (as on your tax return) *</label>
          <input required value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} data-testid="tax-info-legal-name" className={inp} />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Business name / DBA (optional)</label>
          <input value={form.dba} onChange={(e) => setForm({ ...form, dba: e.target.value })} data-testid="tax-info-dba" className={inp} placeholder="LLC or DBA, if any" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Mailing address {needAddr && "*"}</label>
          <textarea required={needAddr} rows={3} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
            data-testid="tax-info-address" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
            placeholder="Street, City, State ZIP" />
        </div>
        {err && <div className="text-sm text-rose-600 font-semibold" data-testid="tax-info-error">{err}</div>}
        <button disabled={busy} data-testid="tax-info-submit" className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-md disabled:opacity-50">
          {busy ? "Saving…" : "Submit securely"}
        </button>
        <p className="text-xs text-slate-400">This link works once. If you need to change something later, contact the office.</p>
      </form>
    </Shell>
  );
}
